if (!customElements.get('hh-motif-browser')) customElements.define('hh-motif-browser', class extends HTMLElement {
  connectedCallback() {
    this.host = this.closest('hh-frame-designer');
    if (!this.host || this.abort) return;
    this.abort = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.launchers ||= this.querySelector('[data-mb-launchers]');
    this.watch = new MutationObserver(() => this.prepare());
    this.watch.observe(this.host, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-blank'] });
    this.prepare();
  }
  disconnectedCallback() {
    this.watch?.disconnect();
    this.abort?.abort();
    this.restore();
    this.launchers?.remove();
    this.host?.classList.remove('hhd-mb-ready');
    this.abort = null;
    this.installed = false;
  }
  prepare() {
    const toolbar = this.host.querySelector('[data-studio-toolbar]');
    if (!toolbar || !this.host.studioReady) return;
    if (this.installed) { this.availability(); return; }
    this.installed = true;
    this.watch.disconnect();
    this.watch.observe(this.host, { attributes: true, attributeFilter: ['data-blank'] });
    toolbar.append(this.launchers);
    this.host.classList.add('hhd-mb-ready');
    const listen = (node, event, fn, extra = {}) => node.addEventListener(event, fn, { ...extra, signal: this.abort.signal });
    listen(this.host, 'click', event => {
      const entry = event.target.closest('[data-mb-open],[data-studio-tool="motif"],[data-all-motifs-open],[data-color-motifs-open],[data-maritim-motifs-open]');
      if (!entry || entry.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(entry.dataset.mbOpen || (entry.hasAttribute('data-color-motifs-open') ? 'color' : 'engraved'), entry, entry.hasAttribute('data-maritim-motifs-open') ? 'maritim' : 'all');
    }, { capture: true });
    listen(this.dialog, 'click', event => {
      const kind = event.target.closest('[data-mb-kind]');
      if (kind) this.setKind(kind.dataset.mbKind);
      if (event.target.closest('[data-mb-close]')) this.dialog.close();
      if (event.target.closest('[data-motif]')) {
        this.chosen = true;
        this.dialog.close();
      }
      if (event.target === this.dialog) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }
    });
    listen(this.dialog, 'keydown', event => event.stopPropagation());
    listen(this.dialog, 'close', () => {
      this.restore();
      if (this.chosen) this.host.focusTextCanvas?.();
      else this.trigger?.focus({ preventScroll: true });
    });
    listen(this.querySelector('[data-mb-category]'), 'change', event => {
      this.host.motifCategory = event.target.value;
      this.host.motifsExpanded = true;
      this.host.filterMotifs();
      this.querySelector('[data-mb-results]').scrollTop = 0;
    });
    this.availability();
  }
  availability() {
    if (!this.installed) return;
    const blank = this.host.blank();
    for (const button of [...this.launchers.querySelectorAll('button'), ...this.querySelectorAll('[data-mb-kind]')]) {
      const kind = button.dataset.mbOpen || button.dataset.mbKind;
      button.disabled = !!blank.fixedMotif || (kind === 'color' && !!blank.engravingOnly);
    }
    this.launchers.querySelector('[data-mb-only]').hidden = !blank.engravingOnly || !!blank.fixedMotif;
    this.launchers.querySelector('[data-mb-fixed]').hidden = !blank.fixedMotif;
  }
  open(kind, trigger, category = 'all') {
    if (this.host.blank().fixedMotif || (kind === 'color' && this.host.blank().engravingOnly)) return;
    this.trigger = trigger;
    this.chosen = false;
    this.moves = [];
    const relocate = (node, target) => {
      if (!node) return;
      const marker = document.createTextNode('');
      node.before(marker);
      this.moves.push({ node, marker });
      target.append(node);
    };
    const search = this.host.querySelector('[data-motif-search]');
    search.value = '';
    this.originalSearchLabel = search.closest('label').querySelector('span').textContent;
    search.closest('label').querySelector('span').textContent = this.dataset.searchLabel;
    relocate(search.closest('label'), this.querySelector('[data-mb-search]'));
    relocate(this.host.querySelector('[data-motif-search-count]'), this.querySelector('[data-mb-summary]'));
    relocate(this.host.querySelector('[data-motif-search-empty]'), this.querySelector('[data-mb-results]'));
    relocate(this.host.querySelector('.hhd-motifs'), this.querySelector('[data-mb-results]'));
    this.setKind(kind, category);
    this.availability();
    this.dialog.showModal();
    this.querySelector('[data-mb-results]').scrollTop = 0;
    this.querySelector('[data-mb-close]').focus({ preventScroll: true });
  }
  setKind(kind, category = 'all') {
    if (kind === 'color' && this.host.blank().engravingOnly) return;
    this.host.querySelector('[data-motif-kind="' + kind + '"]')?.click();
    this.host.dataset.motifKind = kind;
    const available = new Set([...this.host.querySelectorAll('.hhd-motifs [data-motif][data-category]')].filter(item => item.dataset.mode === kind && (!this.host.blank().pendant || kind !== 'color' || item.dataset.category === 'pendant')).map(item => item.dataset.category));
    const select = this.querySelector('[data-mb-category]');
    select.replaceChildren();
    for (const source of this.host.querySelectorAll('[data-motif-category]')) {
      const value = source.dataset.motifCategory;
      if (value === 'color' || source.hidden || (value !== 'all' && !available.has(value))) continue;
      const option = document.createElement('option');
      option.value = value;
      option.textContent = source.textContent;
      select.append(option);
    }
    if (![...select.options].some(option => option.value === category)) category = 'all';
    this.host.motifCategory = category;
    this.host.motifsExpanded = true;
    this.host.filterMotifs();
    this.querySelector('[data-mb-category]').value = category;
    for (const button of this.querySelectorAll('[data-mb-kind]')) button.setAttribute('aria-pressed', String(button.dataset.mbKind === kind));
    this.querySelector('[data-mb-results]').scrollTop = 0;
  }
  restore() {
    const search = this.host?.querySelector('[data-motif-search]');
    if (search && this.originalSearchLabel) search.closest('label').querySelector('span').textContent = this.originalSearchLabel;
    for (const { node, marker } of this.moves || []) marker.replaceWith(node);
    this.moves = [];
  }
});
